import {
    setAvailabilityGroupsQuery,
    useAvailabilityGroupsQuery,
    useBookableResourcesQuery,
    useEventTypesQuery,
} from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import EventTypeSelection from './EventTypeSelection.jsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline/index.js';
import { useState } from 'react';
import NewAvailabilityGroupModal from './NewAvailabilityGroupModal.jsx';

function getEventTypeIds(availabilityGroupId, resource, groups) {
    let enabledTypes = null;
    if (availabilityGroupId) {
        for (const group of groups) {
            if (parseInt(group.id) === parseInt(availabilityGroupId)) {
                enabledTypes = group.applies_to;
                if (
                    !Array.isArray(enabledTypes) &&
                    typeof enabledTypes === 'object'
                ) {
                    enabledTypes = Object.values(enabledTypes);
                }
            }
        }
    } else {
        // Not in a group, event types are whatever are enabled and not in a group
        const allEventTypes = (resource.event_types || []).map(t => t.id);

        enabledTypes = allEventTypes.filter(id => {
            for (const group of groups) {
                if (
                    Object.values(group.applies_to)
                        .map(i => parseInt(i))
                        .indexOf(parseInt(id)) > -1
                ) {
                    return false;
                }
            }

            return true;
        });
    }

    return enabledTypes;
}

function getGroupName(enabledTypes, allTypes) {
    if (!allTypes || !enabledTypes || enabledTypes.length === 0) {
        return 'No Types';
    }

    // Loop by types so it's ordered, return first plus count
    const notShownCount =
        enabledTypes.length > 1 ? ` +${enabledTypes.length - 1} more` : '';
    for (const type of allTypes) {
        if (enabledTypes.indexOf(type.id) > -1) {
            return `${type.name}${notShownCount}`;
        }
    }

    return 'Unknown';
}

const GroupSelectButton = ({ label, isSelected, onClick }) => {
    return (
        <button
            className={`px-4 py-2 text-nowrap flex items-center mr-2 hover:bg-primary-50 border rounded border-primary-700 text-primary-700 ${isSelected ? 'bg-primary-50' : ''}`}
            onClick={onClick}
        >
            <div>{label}</div>
            <ChevronDownIcon className="w-4 h-4 ml-2" />
        </button>
    );
};

const AvailabilityGroups = ({
    resource,
    availabilityGroupId,
    setAvailability,
}) => {
    const { data, isLoading, refetch } = useAvailabilityGroupsQuery(
        resource.id,
    );
    const { refetch: refetchBookableResources } = useBookableResourcesQuery();
    const { data: typeData } = useEventTypesQuery();

    const [recentlyAddedGroup, setRecentlyAddedGroup] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const groups = data || [];

    const { save: saveNewGroupApi, isSaving: isSavingNewGroup } =
        useBusinessPostApi(
            `/scheduling/availability/${resource.id}/availability-groups/0`,
        );
    const { save: saveDelete, isSaving: isDeleting } = useBusinessPostApi(
        `/scheduling/availability/${resource.id}/availability-groups/${availabilityGroupId}/delete`,
    );

    function addNewGroup() {
        setIsModalOpen(true);
    }

    function saveNewGroup(selectedEventTypes) {
        saveNewGroupApi({ event_type_ids: selectedEventTypes }).then(results => {
            setAvailability({
                availabilityGroupId: results.group.id,
                defaultAvailability: results.group.default_availability,
                settings: results.group.settings?.availability_settings,
            });
            setRecentlyAddedGroup(results.group.id);
            setAvailabilityGroupsQuery(resource.id, results.allGroups);
            setIsModalOpen(false);
        });
    }

    /**
     * Select types:
     * - availability group has `applies_to` with a list of types
     * - resource has...???
     * use EventTypeCheckboxes
     */
    const enabledTypes =
        getEventTypeIds(availabilityGroupId, resource, groups) ?? [];

    return (
        <div>
            <NewAvailabilityGroupModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={saveNewGroup}
                isSaving={isSavingNewGroup}
            />

            <div
                className={`flex flex-wrap gap-y-2 items-center ${groups.length === 0 ? 'mb-0' : 'mb-6'}`}
            >
                {groups.length > 0 && (
                    <>
                        <GroupSelectButton
                            label={getGroupName(
                                getEventTypeIds(null, resource, groups),
                                typeData,
                            )}
                            isSelected={!availabilityGroupId}
                            onClick={() => {
                                setAvailability({
                                    availabilityGroupId: null,
                                    defaultAvailability:
                                        resource.default_availability,
                                    settings:
                                        resource.settings
                                            ?.availability_settings,
                                });
                            }}
                        />
                        {groups.map(group => (
                            <GroupSelectButton
                                key={group.id}
                                label={getGroupName(
                                    getEventTypeIds(group.id, resource, groups),
                                    typeData,
                                )}
                                isSelected={availabilityGroupId === group.id}
                                onClick={() => {
                                    setAvailability({
                                        availabilityGroupId: group.id,
                                        defaultAvailability:
                                            group.default_availability,
                                        settings:
                                            group.settings
                                                ?.availability_settings,
                                    });
                                }}
                            />
                        ))}
                    </>
                )}

                {typeData?.length > 1 && (
                    <>
                        {groups.length > 0 && (
                            <div className={`ml-4`}>
                                <button
                                    className={`text-primary-700 hover:underline`}
                                    onClick={addNewGroup}
                                >
                                    {isSavingNewGroup ? 'Adding...' : '+ Add'}
                                </button>
                            </div>
                        )}
                        {groups.length === 0 && (
                            <div
                                className={`ml-4 absolute -top-14 right-16 mb-4`}
                            >
                                <button
                                    className={`btn-light-pill`}
                                    onClick={addNewGroup}
                                >
                                    {isSavingNewGroup
                                        ? 'Adding...'
                                        : 'Set Availability by Type'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="bg-white rounded-t-lg p-4 pt-0.5">
                {!!availabilityGroupId && !enabledTypes?.length && (
                    <button
                        className="text-red-700 my-4 text-right"
                        onClick={() => {
                            saveDelete({}).then(results => {
                                setAvailability({
                                    availabilityGroupId: null,
                                    defaultAvailability:
                                        resource.default_availability,
                                    settings:
                                        resource.settings
                                            ?.availability_settings,
                                });

                                setAvailabilityGroupsQuery(
                                    resource.id,
                                    results,
                                );
                            });
                        }}
                    >
                        {isDeleting ? 'Deleting...' : 'Remove this Schedule'}
                    </button>
                )}
                <EventTypeSelection
                    availabilityGroupId={availabilityGroupId}
                    resource={resource}
                    types={enabledTypes}
                    key={availabilityGroupId}
                    isNewGroup={
                        availabilityGroupId === recentlyAddedGroup &&
                        enabledTypes.length === 0
                    }
                    onSave={() => {
                        setRecentlyAddedGroup(false);
                        //refetch();
                        //refetchBookableResources();
                    }}
                />
            </div>
        </div>
    );
};

export default AvailabilityGroups;
